import React from "react";
import SetupGuideCollapsibleSection from "./SetupGuideCollapsibleSection";
import {
    TROUBLESHOOTING_BY_PROVIDER,
    type SetupGuideTroubleshootingItem,
    type SetupGuideTroubleshootingProvider,
} from "./troubleshootingRegistry";

export type { SetupGuideTroubleshootingItem, SetupGuideTroubleshootingProvider };

const renderDescription = (
    description: string,
    highlights: string[] = [],
): React.ReactNode => {
    if (!highlights.length) {
        return description;
    }

    const pattern = new RegExp(
        `(${highlights.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    );
    const parts = description.split(pattern);

    return parts.map((part, index) =>
        highlights.includes(part) ? (
            <span key={`${part}-${index}`} className="font-semibold text-gray-60">
                {part}
            </span>
        ) : (
            part
        ),
    );
};

interface SetupGuideTroubleshootingProps {
    provider?: SetupGuideTroubleshootingProvider;
    items?: SetupGuideTroubleshootingItem[];
    defaultExpanded?: boolean;
}

const TroubleshootingErrorTag: React.FC<{ label: string }> = ({ label }) => (
    <div className="flex shrink-0 items-center justify-center rounded bg-gray-10 px-1">
        <p className="text-[10px] font-semibold leading-[22px] text-blue-60">{label}</p>
    </div>
);

const TroubleshootingListItem: React.FC<{
    item: SetupGuideTroubleshootingItem;
    showBorder: boolean;
}> = ({ item, showBorder }) => (
    <div
        className={`flex w-full flex-col items-start justify-center gap-2 p-2 ${
            showBorder ? "border-b border-gray-20" : ""
        }`}
    >
        <TroubleshootingErrorTag label={item.error} />
        <p className="min-w-full text-[12px] font-normal leading-[22px] text-gray-60">
            {renderDescription(item.description, item.descriptionHighlights)}
        </p>
    </div>
);

const SetupGuideTroubleshooting: React.FC<SetupGuideTroubleshootingProps> = ({
    provider,
    items,
    defaultExpanded = false,
}) => {
    const resolvedItems = items ?? (provider ? TROUBLESHOOTING_BY_PROVIDER[provider] : []);

    if (!resolvedItems.length) {
        return null;
    }

    return (
        <SetupGuideCollapsibleSection title="Troubleshooting" defaultExpanded={defaultExpanded}>
            <div className="rounded border border-gray-20 bg-white p-2.5">
                <div className="flex w-full flex-col gap-2">
                    {resolvedItems.map((item, index) => (
                        <TroubleshootingListItem
                            key={item.error}
                            item={item}
                            showBorder={index < resolvedItems.length - 1}
                        />
                    ))}
                </div>
            </div>
        </SetupGuideCollapsibleSection>
    );
};

export default React.memo(SetupGuideTroubleshooting);
